import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, AlertTriangle, Package, Boxes } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { AttentionStrip } from '../../components/ui';
import { dashboardAPI, equipmentAPI, inventoryAPI } from '../../services/api';
import AssetTabs from './AssetTabs';
import { isServiceDueSoon } from '../operations/serviceDue';

export default function AssetOverview() {
  const [serviceDueCount, setServiceDueCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [recentItems, setRecentItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      dashboardAPI.getEquipmentSummary().catch(() => ({ data: {} })),
      equipmentAPI.list({ status: 'ACTIVE', disposal_status: 'none', page: 1, limit: 200 })
        .then((res) => {
          const rows = Array.isArray(res?.data) ? res.data : [];
          return rows.filter((eq) => isServiceDueSoon(eq)).length;
        })
        .catch(() => 0),
      inventoryAPI.getLowStockCount().then((res) => res?.data?.count || 0).catch(() => 0),
      inventoryAPI.list({ status: 'ACTIVE', page: 1, limit: 5 })
        .then((res) => ({
          total: res?.total || 0,
          items: Array.isArray(res?.data) ? res.data : [],
        }))
        .catch(() => ({ total: 0, items: [] })),
    ]).then(([summaryRes, dueCount, lowCount, inventoryRes]) => {
      setSummary(summaryRes?.data || {});
      setServiceDueCount(dueCount);
      setLowStockCount(lowCount);
      setTotalItems(inventoryRes.total);
      setRecentItems(inventoryRes.items);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="py-12 text-center text-ga-text-muted">Loading...</div>
      </DashboardLayout>
    );
  }

  const overdue = Array.isArray(summary?.overdue) ? summary.overdue.length : 0;
  const dueIn7Days = Array.isArray(summary?.dueIn7Days) ? summary.dueIn7Days.length : 0;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
            <LayoutGrid className="h-7 w-7 text-ga-primary" />
            Asset Management
          </h1>
          <p className="text-ga-text-muted mt-1">Fixed assets, consumables, and maintenance alerts</p>
        </div>

        <AssetTabs active="overview" />

        {serviceDueCount > 0 && (
          <AttentionStrip
            icon={AlertTriangle}
            tone="warning"
            title={`${serviceDueCount} fixed asset${serviceDueCount === 1 ? '' : 's'} due for service`}
            message="Next service is overdue or falls within the next 7 days."
            action={
              <Link to="/dashboard/assets/fixed" className="ga-btn-primary px-3 py-1.5 text-sm">
                View fixed assets
              </Link>
            }
          />
        )}

        {lowStockCount > 0 && (
          <AttentionStrip
            icon={Boxes}
            tone="danger"
            title={`${lowStockCount} inventory item${lowStockCount === 1 ? '' : 's'} below reorder threshold`}
            message="Restock consumables before they run out during operations."
            action={
              <Link to="/dashboard/assets/inventory?low_stock=true" className="ga-btn-primary px-3 py-1.5 text-sm">
                View low stock
              </Link>
            }
          />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link to="/dashboard/assets/fixed" className="ga-card p-5 hover:border-ga-primary/40 transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <Package className="h-5 w-5 text-ga-primary" />
              <span className="text-sm font-medium text-ga-text-muted">Fixed Assets</span>
            </div>
            <p className="text-2xl font-bold text-ga-text-primary">{serviceDueCount}</p>
            <p className="text-xs text-ga-text-subtle mt-1">Due for service (7-day window)</p>
          </Link>

          <div className="ga-card p-5">
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="h-5 w-5 text-ga-warning" />
              <span className="text-sm font-medium text-ga-text-muted">Overdue service</span>
            </div>
            <p className="text-2xl font-bold text-ga-text-primary">{overdue}</p>
            <p className="text-xs text-ga-text-subtle mt-1">Past due maintenance</p>
          </div>

          <div className="ga-card p-5">
            <div className="flex items-center gap-3 mb-2">
              <Package className="h-5 w-5 text-ga-text-muted" />
              <span className="text-sm font-medium text-ga-text-muted">Due in 7 days</span>
            </div>
            <p className="text-2xl font-bold text-ga-text-primary">{dueIn7Days}</p>
            <p className="text-xs text-ga-text-subtle mt-1">Scheduled soon</p>
          </div>

          <Link to="/dashboard/assets/inventory" className="ga-card p-5 hover:border-ga-primary/40 transition-colors">
            <div className="flex items-center gap-3 mb-2">
              <Boxes className="h-5 w-5 text-ga-primary" />
              <span className="text-sm font-medium text-ga-text-muted">Inventory items</span>
            </div>
            <p className="text-2xl font-bold text-ga-text-primary">{totalItems}</p>
            <p className="text-xs text-ga-text-subtle mt-1">
              {lowStockCount > 0
                ? `${lowStockCount} below reorder threshold`
                : 'Active consumables tracked'}
            </p>
          </Link>
        </div>

        {recentItems.length > 0 && (
          <div className="ga-card p-5">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-bold text-ga-text-primary">Recent inventory</h2>
              <Link to="/dashboard/assets/inventory" className="text-sm text-ga-primary hover:underline">
                View all →
              </Link>
            </div>
            <div className="space-y-3">
              {recentItems.map((item) => (
                <Link
                  key={item.id}
                  to={`/dashboard/assets/inventory/${item.id}`}
                  className="flex items-center justify-between gap-3 rounded-ga-sm border border-ga-border-subtle px-4 py-3 hover:border-ga-primary/40"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-ga-text-primary truncate">{item.item_name}</p>
                    <p className="text-sm text-ga-text-muted">
                      {[item.make, item.colour, item.category || 'Uncategorized'].filter(Boolean).join(' · ')}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-ga-text-primary">
                      {Number(item.quantity_on_hand).toLocaleString()} {item.unit}
                    </p>
                    {item.is_low_stock && (
                      <p className="text-xs text-ga-danger">Low stock</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
